import Download16 from '@carbon/icons-react/es/download/16';
import TrashCan16 from '@carbon/icons-react/es/trash-can/16';
import { Table, Tooltip } from 'antd';
import classNames from 'classnames';
import moment from 'moment';
import React, { FC, useCallback, useEffect, useState } from 'react';
// eslint-disable-next-line no-restricted-imports
import { FormattedDate, FormattedMessage } from 'react-intl';
import { useHistory, useLocation } from 'react-router';
import { FileUploadStatus, UploadFileWithAttachment } from 'api/attachments';
import { Button } from 'components/atoms/button';
import { HumanisedDate } from 'components/atoms/humanised-date';
import { Popconfirm } from 'components/atoms/popconfirm';
import {
  BasicImagePreview,
  fileFormatNotAbleToPreview,
  localStorageWorkspacePath,
  nonImageFileToPreview,
} from 'components/organisms/image-preview';
import { RbacButton } from 'components/organisms/rbac/components/rbac-buttons';
import { RoutePaths } from 'constant';
import { ProjectResource } from 'hooks/rbac/useWithAccess';
import { useLocalStorageSession } from 'hooks/use-local-storage-session';
import { useTranslation } from 'hooks/use-translation';
import { handleDownload } from 'utils/attachment';
import { extname, isImageUrlFromExtension, openFileUrl } from 'utils/files';
import styles from './attachments.module.css';

interface RenderAttachmentNameProps {
  text: string;
  record: UploadFileWithAttachment;
}

const RenderAttachmentName: FC<RenderAttachmentNameProps> = ({
  text,
  record,
}) => {
  const history = useHistory();
  const [isImagePreviewVisible, setImagePreviewState] = useState(false);
  const [fileExtension, setFileExtension] = useState('');
  const [isImage, setIsImage] = useState(false);
  const { pathname } = useLocation();
  const { setItem } = useLocalStorageSession(
    localStorageWorkspacePath,
    pathname,
  );
  const downloadAttachmentErrorText = useTranslation(
    'attachments.download.error',
  );

  const openFileInWorkspace = useCallback(() => {
    setItem();
    if (record?.attachment?.attachmentId) {
      history.push(
        RoutePaths.attachmentWorkspace.replaceAll(
          ':attachmentId',
          record?.attachment?.attachmentId.toString(),
        ),
      );
    }
  }, [history, record?.attachment?.attachmentId, setItem]);

  const onImageOnClick = useCallback(
    (e) => {
      e.stopPropagation();
      if (!record?.attachment?.isAnnotationStarted) {
        setImagePreviewState(true);
      } else {
        openFileInWorkspace();
      }
    },
    [openFileInWorkspace, record?.attachment?.isAnnotationStarted],
  );

  const onNonImageOnClick = useCallback(
    (e) => {
      e.stopPropagation();
      if (fileFormatNotAbleToPreview.includes(fileExtension)) {
        openFileInWorkspace();
      } else if (nonImageFileToPreview.includes(fileExtension)) {
        if (!record?.attachment?.isAnnotationStarted) {
          setImagePreviewState(true);
        } else {
          openFileInWorkspace();
        }
      } else {
        openFileUrl(
          e,
          record?.attachment?.attachmentId,
          downloadAttachmentErrorText,
        );
      }
    },
    [
      downloadAttachmentErrorText,
      fileExtension,
      openFileInWorkspace,
      record?.attachment?.attachmentId,
      record?.attachment?.isAnnotationStarted,
    ],
  );

  useEffect(() => {
    const fileExtenionValue = extname((record.url || record.fileName) ?? '');

    if (fileExtenionValue) {
      setFileExtension(fileExtenionValue.toLowerCase());
    }
    setIsImage(isImageUrlFromExtension((record.url || record.fileName) ?? ''));
  }, [record.url, record.fileName]);

  return (
    <>
      <Tooltip title={text} placement="topLeft">
        {isImage ? (
          <div
            role="button"
            tabIndex={0}
            onClick={onImageOnClick}
            onKeyPress={onImageOnClick}
          >
            {text}
          </div>
        ) : (
          // eslint-disable-next-line jsx-a11y/anchor-is-valid,jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
          <a
            rel="noreferrer noopener"
            target="_blank"
            className={classNames(styles.name, 'text-01')}
            onClick={onNonImageOnClick}
          >
            {text}
          </a>
        )}
      </Tooltip>
      <BasicImagePreview
        data={record?.attachment?.attachmentId}
        alt={record.fileName}
        fileName={record.fileName ?? ''}
        loading="lazy"
        src={record.url}
        preview={isImagePreviewVisible}
        onClose={() => setImagePreviewState(false)}
        onDownload={() =>
          handleDownload(
            record?.attachment?.attachmentId,
            downloadAttachmentErrorText,
          )
        }
        isBasicPreview={false}
      />
    </>
  );
};

interface AttachmentsTableProps {
  filesList?: Array<UploadFileWithAttachment>;
  onRemoveFile: (file: UploadFileWithAttachment) => void;
  onDownloadFile: (file: UploadFileWithAttachment) => void;
  projectResource?: ProjectResource;
}

export const AttachmentsTable: FC<AttachmentsTableProps> = (props) => {
  const {
    filesList = [],
    onRemoveFile,
    onDownloadFile,
    projectResource,
  } = props;
  const ATTACHMENTS_COLUMNS = [
    {
      title: useTranslation('name'),
      dataIndex: 'name',
      key: 'name',
      width: '50%',
      ellipsis: true,
      render: (text, record: UploadFileWithAttachment) => (
        <RenderAttachmentName text={text} record={record} />
      ),
    },
    {
      title: useTranslation('uploaded_on'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      className: styles.uploaded_on,
      render: (text, row: UploadFileWithAttachment) => {
        const createdAt = row.attachment?.createdAt;

        if (row.status === FileUploadStatus.Uploading) {
          return (
            <div className="text-03">
              <FormattedMessage id="uploading" />
            </div>
          );
        }
        if (row.status === FileUploadStatus.Error) {
          return <FormattedMessage id="attachment.failed" />;
        }
        if (createdAt) {
          return (
            <Tooltip
              title={
                <FormattedDate
                  value={createdAt}
                  day="numeric"
                  month="short"
                  year="numeric"
                  hour="numeric"
                  minute="numeric"
                />
              }
              placement="topLeft"
            >
              <HumanisedDate timestamp={moment(createdAt)} />
            </Tooltip>
          );
        }

        return <div className="text-03">---</div>;
      },
    },
    {
      className: styles.delete_cell,
      render: (text, item: UploadFileWithAttachment) => {
        return (
          <>
            <Button
              icon={<Download16 />}
              type="link"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onDownloadFile(item);
              }}
            />
            {item.status === FileUploadStatus.Error ? (
              <Button
                icon={<TrashCan16 />}
                type="link"
                size="small"
                danger
                onClick={(e) => {
                  e.stopPropagation();
                  onRemoveFile(item);
                }}
              />
            ) : (
              <Popconfirm
                onConfirm={() => {
                  onRemoveFile(item);
                }}
              >
                <RbacButton
                  icon={<TrashCan16 />}
                  type="link"
                  size="small"
                  danger
                  rule="TASK_ATTACHMENT:WRITE"
                  resourceData={projectResource}
                />
              </Popconfirm>
            )}
          </>
        );
      },
    },
  ];

  return (
    <Table
      className={styles.attachments__table}
      size="small"
      rowKey="uid"
      pagination={false}
      columns={ATTACHMENTS_COLUMNS}
      dataSource={filesList}
    />
  );
};
